import { FC, useState } from 'react';
import styled, { DefaultTheme } from 'styled-components';

import Text from '@/components/Text';

const InfoIconHolder = styled.span`
  cursor: pointer;
  position: relative;
  flex-shrink: 0;
  svg {
    color: ${({ theme }) => theme.colors.warning};
  }
`;

const TooltipBox = styled.div`
  position: absolute;
  padding: 4px 8px;
  width: max-content;
  z-index: 11;
  background: ${({ theme }) => theme.colors.white};
  box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
  border-radius: 4px;
  bottom: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  text-align: center;
`;

const Triangle = styled.div`
  width: 26px;
  height: 14px;
  position: absolute;
  overflow: hidden;
  bottom: -14px;
  left: 0;
  right: 0;
  margin: auto;
  &::after {
    content: '';
    position: absolute;
    width: 12px;
    height: 12px;
    background: ${({ theme }) => theme.colors.white};
    transform: rotate(45deg);
    top: -8px;
    left: 0;
    right: 0;
    margin: auto;
    box-shadow: 0px 0px 8px rgba(37, 40, 43, 0.12);
    border: 1px solid ${({ theme }) => theme.colors.lightGray};
  }
`;

const BADGE_LABELS = {
  user: 'Backbone user',
  freelancer: 'Freelancer',
  generated: 'Generated invoice',
  imported: 'Imported Invoice',
} as const;

type BadgeType = keyof typeof BADGE_LABELS;

const BADGE_COLORS: Record<BadgeType, keyof DefaultTheme['colors']> = {
  user: 'purple',
  freelancer: 'success',
  generated: 'warning',
  imported: 'optimisation',
};

const Badge = styled.div<{ $type: BadgeType }>`
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: ${({ theme, $type }) => theme.colors[BADGE_COLORS[$type]]};
  outline: 2px solid
    ${({ theme, $type }) => `${theme.colors[BADGE_COLORS[$type]]}40`};
`;

interface Props {
  type?: BadgeType;
}

const BackboneUserBadge: FC<Props> = ({ type = 'user' }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  const label = BADGE_LABELS[type];

  return (
    <>
      <InfoIconHolder
        onMouseOver={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
      >
        <Badge $type={type} />
        {!!showTooltip && (
          <TooltipBox>
            <Triangle />
            <Text $type="extraSmall">{label}</Text>
          </TooltipBox>
        )}
      </InfoIconHolder>
    </>
  );
};

export default BackboneUserBadge;
